import { authClient } from "@/lib/auth-client";
import { passwordSchema } from "@/lib/password-schema";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

const searchSchema = z.object({
	token: z.string().optional(),
});

export const Route = createFileRoute("/reset-password")({
	component: ResetPasswordPage,
	validateSearch: searchSchema,
});

function ResetPasswordPage() {
	const { token } = Route.useSearch();
	const navigate = useNavigate();

	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	if (!token) {
		return (
			<Shell title="Reset your password">
				<p className="text-sm text-[#dc2626]">
					This page needs a token. Open the link from your reset email.
				</p>
				<Link
					to="/login"
					className="mt-4 inline-flex h-9 items-center justify-center rounded border border-[#d1d5db] px-4 text-sm font-medium text-[#1e1e20] hover:bg-[#f3f4f6]"
				>
					Back to sign in
				</Link>
			</Shell>
		);
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError(null);

		const parsed = passwordSchema.safeParse(newPassword);
		if (!parsed.success) {
			setError(parsed.error.issues[0]?.message ?? "Invalid password");
			return;
		}
		if (newPassword !== confirmPassword) {
			setError("Passwords don't match");
			return;
		}

		setLoading(true);
		try {
			const result = await authClient.resetPassword({ newPassword, token });
			if (result.error) {
				setError(result.error.message ?? "Reset failed");
				return;
			}
			toast.success("Password updated. Sign in with your new password.");
			await navigate({ to: "/login" });
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Reset failed");
		} finally {
			setLoading(false);
		}
	}

	return (
		<Shell title="Set a new password">
			<form onSubmit={handleSubmit} className="space-y-4">
				{error && <div className="rounded bg-[#fef2f2] p-3 text-sm text-[#dc2626]">{error}</div>}

				<div className="space-y-1">
					<label htmlFor="new-password" className="text-xs font-medium text-[#1e1e20]">
						New password
					</label>
					<input
						id="new-password"
						type="password"
						value={newPassword}
						onChange={(e) => setNewPassword(e.target.value)}
						required
						autoComplete="new-password"
						className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] placeholder:text-[#9ca3af] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
						placeholder="At least 12 characters"
					/>
				</div>

				<div className="space-y-1">
					<label htmlFor="confirm-password" className="text-xs font-medium text-[#1e1e20]">
						Confirm password
					</label>
					<input
						id="confirm-password"
						type="password"
						value={confirmPassword}
						onChange={(e) => setConfirmPassword(e.target.value)}
						required
						autoComplete="new-password"
						className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] placeholder:text-[#9ca3af] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
					/>
				</div>

				<button
					type="submit"
					disabled={loading}
					className="h-9 w-full rounded bg-[#16a34a] text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#15803d] disabled:opacity-50"
				>
					{loading ? "Updating…" : "Update password"}
				</button>

				<Link to="/login" className="block text-center text-xs text-[#6b7280] hover:text-[#1e1e20]">
					Back to sign in
				</Link>
			</form>
		</Shell>
	);
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<div className="flex min-h-screen items-center justify-center bg-[#f8f9fb]">
			<div className="w-full max-w-sm space-y-6 rounded-lg border border-[#e5e7eb] bg-white p-8 shadow-sm">
				<div className="text-center">
					<div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-[#16a34a]">
						<span className="text-lg font-bold text-white">P</span>
					</div>
					<h1 className="text-xl font-semibold text-[#1e1e20]">{title}</h1>
				</div>
				{children}
			</div>
		</div>
	);
}
